import type { GeoPoint, PermissionStatus } from '@metro/location'
import { reportAnalyticsEvent } from './webAnalyticsReporter'

/**
 * Location / geolocation analytics — byte-identical event names + params
 * with iOS `MetroCore/Sources/Location/LocationAnalytics.swift`
 * (cross-platform contract). Web routes them through the existing
 * `reportAnalyticsEvent` sink (GA4 + Amplitude), which is
 * itself production + consent gated and fail-safe — these builders just
 * shape the payload.
 *
 * Web mirrors the iOS Location reducer seam-for-seam: every iOS reducer
 * action that has an equivalent web `@metro/location` store action /
 * consumer-service operation emits the same event. All 8 iOS events are
 * emitted on web:
 *
 *     location_user_location_requested {}                → MyLocation button tap
 *                                                          (iOS `.requestUserLocation`)
 *     location_initial_user_location_requested {}        → app-start auto-locate
 *                                                          path (iOS
 *                                                          `.requestInitialUserLocation`)
 *     location_requested {}                              → generic fix request /
 *                                                          `RequestFix` (iOS
 *                                                          `.requestLocation`)
 *     location_authorization_requested {}                → browser permission prompt
 *                                                          triggered (iOS
 *                                                          `.requestAuthorization`)
 *     location_authorization_changed { authorization_status }
 *                                                        → permission resolved /
 *                                                          changed (iOS
 *                                                          `.didChangeAuthorization`)
 *     location_updated { accuracy, timestamp }           → successful fix (iOS
 *                                                          `.didUpdateLocations`)
 *     location_latest_requested {}                       → cached-latest read
 *                                                          (iOS `.getLatestLocation`)
 *
 * **No raw coordinates (G-7, no-PII rule).** None of these events emit
 * `latitude` / `longitude` or a `"lat,lon"` location string. iOS dropped raw
 * coordinates from its location surface (precise location to AppMetrica /
 * Amplitude is a privacy exposure with no product question it answers); web
 * mirrors that — `location_updated` ships only `accuracy` + `timestamp`, and
 * the two cached-read events ship no params. Event NAMES are unchanged so the
 * cross-platform dashboard wiring is unaffected.
 */

/**
 * Browser permission state → iOS `CLAuthorizationStatus` string (byte-identical
 * vocabulary). Web's Permissions API has no always/when-in-use split, so a
 * granted permission maps to `authorized_when_in_use` to share the iOS
 * "granted" bucket on the dashboard. `restricted` mirrors iOS verbatim.
 */
export function authorizationStatusString(status: PermissionStatus): string {
  switch (status) {
    case 'granted':
      return 'authorized_when_in_use'
    case 'denied':
      return 'denied'
    case 'restricted':
      return 'restricted'
    case 'notDetermined':
      return 'not_determined'
  }
}

/** `location_user_location_requested` — the explicit "find me" tap. */
export function reportUserLocationRequested(): void {
  reportAnalyticsEvent('location_user_location_requested', {})
}

/**
 * `location_initial_user_location_requested` — the app-start auto-locate
 * path (mirrors iOS `.requestInitialUserLocation`). Web only runs this when
 * permission is already granted at launch (it never prompts on start).
 */
export function reportInitialUserLocationRequested(): void {
  reportAnalyticsEvent('location_initial_user_location_requested', {})
}

/** `location_requested` — a generic fix request (`RequestFix`). */
export function reportLocationRequested(): void {
  reportAnalyticsEvent('location_requested', {})
}

/**
 * `location_latest_requested` — a cached-latest read (iOS `.getLatestLocation`).
 *
 * G-7 (no-PII rule): the former `location: "lat,lon"` coordinate-string param
 * is **dropped** — no raw coordinates leave the device. The event still fires
 * (the lifecycle signal — "a cached location was read" — is the useful, non-PII
 * part) but now carries no params, matching iOS after its G-7 PII drop.
 */
export function reportLatestLocationRequested(): void {
  reportAnalyticsEvent('location_latest_requested', {})
}

/**
 * `location_authorization_requested` — fired when the browser permission
 * prompt is about to appear (the first `getCurrentPosition` that prompts,
 * i.e. status was `notDetermined`).
 */
export function reportAuthorizationRequested(): void {
  reportAnalyticsEvent('location_authorization_requested', {})
}

/**
 * `location_authorization_changed` — fired when a permission status is
 * learned / changes. `authorization_status` carries the iOS-vocabulary
 * string.
 */
export function reportAuthorizationChanged(status: PermissionStatus): void {
  reportAnalyticsEvent('location_authorization_changed', {
    authorization_status: authorizationStatusString(status),
  })
}

/**
 * `location_updated` — fired on a successful position fix. Carries only the
 * non-PII fix metadata: `accuracy` (metres) + `timestamp` (seconds since
 * epoch). When the fix carries no metadata (e.g. a synthetic / test point),
 * `accuracy` defaults to 0 and `timestamp` to the host clock so the param
 * shape stays uniform.
 *
 * G-7 (no-PII rule): raw `latitude` / `longitude` are **deliberately NOT
 * emitted** — precise coordinates leaving the device to AppMetrica /
 * Amplitude is a privacy exposure with no product question it answers, and
 * iOS dropped them (Location/LocationKit now ship only accuracy+timestamp).
 * This mirrors that drop for cross-platform parity. Do NOT re-add the
 * coordinates; bucket on the dashboard if a coarse region is ever needed.
 */
export function reportLocationUpdated(point: GeoPoint): void {
  reportAnalyticsEvent('location_updated', {
    accuracy: point.accuracy ?? 0,
    timestamp: point.timestamp ?? Date.now() / 1000,
  })
}
